import * as React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Download,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Users,
  IndianRupee,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
} from "lucide-react";

// Mock subscription data
const subscriptions = [
  {
    id: "SUB001",
    agentId: "AGT001",
    agentName: "Wanderlust Travel Co.",
    agentEmail: "contact@wanderlust.com",
    plan: "Growth",
    status: "active",
    amount: 3499,
    billingCycle: "yearly",
    startDate: "2024-01-15",
    endDate: "2025-01-15",
    nextBilling: "2025-01-15",
    paymentMethod: "Credit Card",
    lastPayment: "2024-01-15",
    autoRenew: true,
    usage: {
      packages: 12,
      maxPackages: 50,
      bookings: 89,
      maxBookings: 500,
    },
  },
  {
    id: "SUB002",
    agentId: "AGT002",
    agentName: "Adventure Tours Ltd.",
    agentEmail: "info@adventure.com",
    plan: "Pro",
    status: "active",
    amount: 9999,
    billingCycle: "yearly",
    startDate: "2024-02-01",
    endDate: "2025-02-01",
    nextBilling: "2025-02-01",
    paymentMethod: "Bank Transfer",
    lastPayment: "2024-02-01",
    autoRenew: true,
    usage: {
      packages: 45,
      maxPackages: -1,
      bookings: 234,
      maxBookings: -1,
    },
  },
  {
    id: "SUB003",
    agentId: "AGT003",
    agentName: "Dream Destinations",
    agentEmail: "hello@dream.com",
    plan: "Trial",
    status: "expired",
    amount: 0,
    billingCycle: "trial",
    startDate: "2024-03-01",
    endDate: "2024-03-15",
    nextBilling: null,
    paymentMethod: null,
    lastPayment: null,
    autoRenew: false,
    usage: {
      packages: 3,
      maxPackages: 5,
      bookings: 2,
      maxBookings: 10,
    },
  },
  {
    id: "SUB004",
    agentId: "AGT004",
    agentName: "Exotic Travels",
    agentEmail: "support@exotic.com",
    plan: "Growth",
    status: "payment_failed",
    amount: 3499,
    billingCycle: "yearly",
    startDate: "2024-01-20",
    endDate: "2025-01-20",
    nextBilling: "2024-12-20",
    paymentMethod: "Credit Card",
    lastPayment: "2024-01-20",
    autoRenew: true,
    usage: {
      packages: 8,
      maxPackages: 50,
      bookings: 45,
      maxBookings: 500,
    },
  },
  {
    id: "SUB005",
    agentId: "AGT005",
    agentName: "Coastal Adventures",
    agentEmail: "info@coastal.com",
    plan: "Growth",
    status: "cancelled",
    amount: 3499,
    billingCycle: "yearly",
    startDate: "2024-01-10",
    endDate: "2024-12-10",
    nextBilling: null,
    paymentMethod: "Credit Card",
    lastPayment: "2024-01-10",
    autoRenew: false,
    usage: {
      packages: 15,
      maxPackages: 50,
      bookings: 67,
      maxBookings: 500,
    },
  },
];

const planConfig = {
  Trial: {
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
  },
  Growth: {
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  Pro: {
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
  },
};

const statusConfig = {
  active: {
    label: "Active",
    color: "text-green-600",
    bgColor: "bg-green-50",
    icon: CheckCircle,
  },
  expired: {
    label: "Expired",
    color: "text-red-600",
    bgColor: "bg-red-50",
    icon: XCircle,
  },
  payment_failed: {
    label: "Payment Failed",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    icon: AlertTriangle,
  },
  cancelled: {
    label: "Cancelled",
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    icon: XCircle,
  },
  pending: {
    label: "Pending",
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    icon: Clock,
  },
};

export default function SubscriptionsPage() {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [planFilter, setPlanFilter] = React.useState("all");
  const [selectedSubscription, setSelectedSubscription] =
    React.useState<any>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingSubscription, setEditingSubscription] =
    React.useState<any>(null);
  const { toast } = useToast();

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch =
      sub.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.agentEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || sub.status === statusFilter;
    const matchesPlan = planFilter === "all" || sub.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const stats = {
    totalSubscriptions: subscriptions.length,
    activeSubscriptions: subscriptions.filter((s) => s.status === "active")
      .length,
    expiredSubscriptions: subscriptions.filter((s) => s.status === "expired")
      .length,
    failedPayments: subscriptions.filter((s) => s.status === "payment_failed")
      .length,
    totalRevenue: subscriptions
      .filter((s) => s.status === "active")
      .reduce((sum, s) => sum + s.amount, 0),
  };

  // const handleUpgradeDowngrade = (subscriptionId: string, newPlan: string) => {
  //   toast({
  //     title: "Plan Updated",
  //     description: `Subscription ${subscriptionId} has been updated to ${newPlan} plan.`,
  //   });
  // };

  const handleRetryPayment = (subscriptionId: string) => {
    toast({
      title: "Payment Retry Initiated",
      description: `Payment retry has been initiated for subscription ${subscriptionId}.`,
    });
  };

  const handleCancelSubscription = (subscriptionId: string) => {
    toast({
      title: "Subscription Cancelled",
      description: `Subscription ${subscriptionId} has been cancelled.`,
      variant: "destructive",
    });
  };

  const handleViewDetails = (subscription: any) => {
    setSelectedSubscription(subscription);
    setIsViewDialogOpen(true);
  };

  const handleEditPlan = (subscription: any) => {
    setEditingSubscription(subscription);
    setIsEditDialogOpen(true);
  };

  const handleUpdatePlan = async (
    subscriptionId: string,
    newPlan: string,
    newAmount: number
  ) => {
    try {
      // Simulate API call to update subscription
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast({
        title: "Plan Updated",
        description: `Subscription ${subscriptionId} has been updated to ${newPlan} plan (₹${newAmount}).`,
      });

      setIsEditDialogOpen(false);
      setEditingSubscription(null);
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update subscription plan. Please try again.",
        variant: "destructive",
      });
    }
  };

  const exportSubscriptionData = () => {
    try {
      const csvData = filteredSubscriptions.map((subscription) => ({
        ID: subscription.id,
        Agent_Name: subscription.agentName,
        Agent_Email: subscription.agentEmail,
        Plan: subscription.plan,
        Status: subscription.status,
        Amount: subscription.amount,
        Billing_Cycle: subscription.billingCycle,
        Start_Date: subscription.startDate,
        End_Date: subscription.endDate,
        Next_Billing: subscription.nextBilling || "N/A",
        Payment_Method: subscription.paymentMethod || "N/A",
        Auto_Renew: subscription.autoRenew ? "Yes" : "No",
        Packages_Used: subscription.usage.packages,
        Max_Packages:
          subscription.usage.maxPackages === -1
            ? "Unlimited"
            : subscription.usage.maxPackages,
        Bookings_Used: subscription.usage.bookings,
        Max_Bookings:
          subscription.usage.maxBookings === -1
            ? "Unlimited"
            : subscription.usage.maxBookings,
      }));

      // Convert to CSV string
      const headers = Object.keys(csvData[0]).join(",");
      const rows = csvData.map((row) => Object.values(row).join(","));
      const csvContent = [headers, ...rows].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `subscription-data-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export Successful",
        description: `${csvData.length} subscription records exported to CSV.`,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export subscription data. Please try again.",
        variant: "destructive",
      });
    }
  };

  const syncBillingData = async () => {
    try {
      // Simulate API call to sync billing data
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast({
        title: "Billing Sync Complete",
        description:
          "Successfully synchronized billing data with payment providers.",
      });
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: "Failed to sync billing data. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number) => {
    if (amount === 0) return "Free";
    return (
      <>
        <span className="rupee-font">₹</span>
        {amount.toLocaleString("en-IN")}
      </>
    );
  };

  const getUsagePercentage = (used: number, max: number) => {
    if (max === -1) return 0; // Unlimited
    return Math.round((used / max) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Subscription Management
          </h1>
          <p className="text-gray-600 mt-2">
            Monitor and manage agent subscription plans and billing
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={exportSubscriptionData}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={syncBillingData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Billing
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Subscriptions
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalSubscriptions}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.activeSubscriptions}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Expired</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.expiredSubscriptions}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Payment Issues
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats.failedPayments}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Monthly Revenue
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  <span className="rupee-font">₹</span>
                  {Math.round(stats.totalRevenue / 12).toLocaleString("en-IN")}
                </p>
              </div>
              <IndianRupee className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by agent name, email, or subscription ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="payment_failed">Payment Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="Trial">Trial</SelectItem>
                <SelectItem value="Growth">Growth</SelectItem>
                <SelectItem value="Pro">Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Details</CardTitle>
          <CardDescription>
            Showing {filteredSubscriptions.length} of {subscriptions.length}{" "}
            subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Next Billing</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.map((subscription) => {
                  const statusInfo =
                    statusConfig[
                      subscription.status as keyof typeof statusConfig
                    ];
                  const planInfo =
                    planConfig[subscription.plan as keyof typeof planConfig];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900">
                            {subscription.agentName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {subscription.agentEmail}
                          </div>
                          <div className="text-xs text-gray-400">
                            ID: {subscription.id}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`${planInfo.bgColor} ${planInfo.color} ${planInfo.borderColor}`}
                        >
                          {subscription.plan}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <StatusIcon
                            className={`h-4 w-4 ${statusInfo.color}`}
                          />
                          <Badge
                            variant="outline"
                            className={`${statusInfo.bgColor} ${statusInfo.color}`}
                          >
                            {statusInfo.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {formatAmount(subscription.amount)}
                          </div>
                          {subscription.amount > 0 && (
                            <div className="text-sm text-gray-500 capitalize">
                              {subscription.billingCycle}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm">
                            Packages: {subscription.usage.packages}
                            {subscription.usage.maxPackages > 0 &&
                              ` / ${subscription.usage.maxPackages}`}
                            {subscription.usage.maxPackages === -1 &&
                              " / Unlimited"}
                          </div>
                          <div className="text-sm">
                            Bookings: {subscription.usage.bookings}
                            {subscription.usage.maxBookings > 0 &&
                              ` / ${subscription.usage.maxBookings}`}
                            {subscription.usage.maxBookings === -1 &&
                              " / Unlimited"}
                          </div>
                          {subscription.usage.maxPackages > 0 && (
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div
                                className="bg-blue-600 h-1 rounded-full"
                                style={{
                                  width: `${getUsagePercentage(
                                    subscription.usage.packages,
                                    subscription.usage.maxPackages
                                  )}%`,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {subscription.nextBilling ? (
                          <div>
                            <div className="text-sm font-medium">
                              {formatDate(subscription.nextBilling)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {subscription.autoRenew ? "Auto-renew" : "Manual"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleViewDetails(subscription)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleEditPlan(subscription)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Plan
                            </DropdownMenuItem>
                            {subscription.status === "payment_failed" && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRetryPayment(subscription.id)
                                }
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Retry Payment
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() =>
                                handleCancelSubscription(subscription.id)
                              }
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Cancel Subscription
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Subscription Details</DialogTitle>
            <DialogDescription>
              Detailed information about the subscription
            </DialogDescription>
          </DialogHeader>
          {selectedSubscription && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Agent Information</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Agent Name
                      </Label>
                      <p className="text-lg font-semibold">
                        {selectedSubscription.agentName}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Agent Email
                      </Label>
                      <p className="text-lg">
                        {selectedSubscription.agentEmail}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Agent ID
                      </Label>
                      <p className="text-lg font-mono">
                        {selectedSubscription.agentId}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">
                    Subscription Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Subscription ID
                      </Label>
                      <p className="text-lg font-mono">
                        {selectedSubscription.id}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Plan
                      </Label>
                      <Badge className="text-lg">
                        {selectedSubscription.plan}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Status
                      </Label>
                      <Badge
                        className={
                          selectedSubscription.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {selectedSubscription.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Billing Information</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Amount
                      </Label>
                      <p className="text-2xl font-bold text-green-600">
                        <span className="rupee-font">₹</span>
                        {selectedSubscription.amount.toLocaleString("en-IN")}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Billing Cycle
                      </Label>
                      <p className="text-lg capitalize">
                        {selectedSubscription.billingCycle}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Payment Method
                      </Label>
                      <p className="text-lg">
                        {selectedSubscription.paymentMethod}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Auto Renew
                      </Label>
                      <Badge
                        className={
                          selectedSubscription.autoRenew
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }
                      >
                        {selectedSubscription.autoRenew ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">Usage Information</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Packages Used
                      </Label>
                      <p className="text-lg">
                        {selectedSubscription.usage.packages}
                        {selectedSubscription.usage.maxPackages > 0 &&
                          ` / ${selectedSubscription.usage.maxPackages}`}
                        {selectedSubscription.usage.maxPackages === -1 &&
                          " / Unlimited"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Bookings Used
                      </Label>
                      <p className="text-lg">
                        {selectedSubscription.usage.bookings}
                        {selectedSubscription.usage.maxBookings > 0 &&
                          ` / ${selectedSubscription.usage.maxBookings}`}
                        {selectedSubscription.usage.maxBookings === -1 &&
                          " / Unlimited"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Start Date
                  </Label>
                  <p className="text-lg">
                    {formatDate(selectedSubscription.startDate)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    End Date
                  </Label>
                  <p className="text-lg">
                    {formatDate(selectedSubscription.endDate)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Next Billing
                  </Label>
                  <p className="text-lg">
                    {selectedSubscription.nextBilling
                      ? formatDate(selectedSubscription.nextBilling)
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">
                  Last Payment
                </Label>
                <p className="text-lg">
                  {formatDate(selectedSubscription.lastPayment)}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setIsViewDialogOpen(false);
                if (selectedSubscription) handleEditPlan(selectedSubscription);
              }}
            >
              Edit Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the subscription plan and billing details
            </DialogDescription>
          </DialogHeader>
          {editingSubscription && (
            <SubscriptionEditForm
              subscription={editingSubscription}
              onSave={handleUpdatePlan}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setEditingSubscription(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Subscription Edit Form Component
const SubscriptionEditForm: React.FC<{
  subscription: any;
  onSave: (subscriptionId: string, newPlan: string, newAmount: number) => void;
  onCancel: () => void;
}> = ({ subscription, onSave, onCancel }) => {
  const [formData, setFormData] = React.useState({
    plan: subscription.plan,
    amount: subscription.amount,
    billingCycle: subscription.billingCycle,
    autoRenew: subscription.autoRenew,
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const planOptions = [
    { id: "basic", name: "Basic", amount: 999, packages: 10, bookings: 100 },
    { id: "growth", name: "Growth", amount: 3499, packages: 50, bookings: 500 },
    { id: "pro", name: "Pro", amount: 9999, packages: -1, bookings: -1 }, // -1 means unlimited
    {
      id: "enterprise",
      name: "Enterprise",
      amount: 19999,
      packages: -1,
      bookings: -1,
    },
  ];

  const handlePlanChange = (newPlan: string) => {
    const selectedPlan = planOptions.find((p) => p.id === newPlan);
    if (selectedPlan) {
      setFormData((prev) => ({
        ...prev,
        plan: newPlan,
        amount: selectedPlan.amount,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSave(subscription.id, formData.plan, formData.amount);
    } catch (error) {
      console.error("Error updating subscription:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="plan">Plan</Label>
          <Select value={formData.plan} onValueChange={handlePlanChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {planOptions.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{plan.name}</span>
                    <span className="text-sm text-gray-500">
                      ₹{plan.amount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="amount">Amount (₹)</Label>
          <Input
            id="amount"
            type="number"
            value={formData.amount}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                amount: parseFloat(e.target.value),
              }))
            }
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="billingCycle">Billing Cycle</Label>
          <Select
            value={formData.billingCycle}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, billingCycle: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2 pt-6">
          <input
            type="checkbox"
            id="autoRenew"
            checked={formData.autoRenew}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, autoRenew: e.target.checked }))
            }
            className="rounded"
          />
          <Label htmlFor="autoRenew">Auto Renew</Label>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Plan Features</h4>
        {(() => {
          const selectedPlan = planOptions.find((p) => p.id === formData.plan);
          return selectedPlan ? (
            <div className="space-y-1 text-sm">
              <div>
                • Packages:{" "}
                {selectedPlan.packages === -1
                  ? "Unlimited"
                  : selectedPlan.packages}
              </div>
              <div>
                • Bookings:{" "}
                {selectedPlan.bookings === -1
                  ? "Unlimited"
                  : selectedPlan.bookings}
              </div>
              <div>• Priority Support</div>
              <div>• Advanced Analytics</div>
            </div>
          ) : null;
        })()}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Updating..." : "Update Plan"}
        </Button>
      </DialogFooter>
    </form>
  );
};
